'use client';

import { useActionState, useEffect, useId, useState } from 'react';
import { useFormStatus } from 'react-dom';
import { joinWaitlist, type WaitlistState } from '@/app/_actions/waitlist';

const INITIAL: WaitlistState = { status: 'idle' };
const TOAST_DURATION_MS = 5000;

type MessageType = 'success' | 'info' | 'error';
type Message = { text: string; type: MessageType } | null;

function messageFor(state: WaitlistState): Message {
  switch (state.status) {
    case 'success':
      return {
        text: "You're on the list. We'll be in touch.",
        type: 'success',
      };
    case 'already_subscribed':
      return {
        text: "Already on the list — we'll be in touch.",
        type: 'info',
      };
    case 'error':
      return { text: state.message, type: 'error' };
    default:
      return null;
  }
}

export function WaitlistForm({ tone = 'light' }: { tone?: 'light' | 'dark' }) {
  const [state, formAction] = useActionState(joinWaitlist, INITIAL);
  const [email, setEmail] = useState('');
  const emailId = useId();
  const isDark = tone === 'dark';

  const message = messageFor(state);
  const isSubmitted =
    state.status === 'success' || state.status === 'already_subscribed';
  const hasText = email.trim().length > 0;

  return (
    <>
      <form action={formAction} className='w-full max-w-[26rem]' noValidate>
        <label htmlFor={emailId} className='sr-only'>
          Email address
        </label>

        {/* Mobile: minimalist single field with embedded "Join →" button */}
        <div
          className={`relative h-12 md:hidden ${
            isDark
              ? 'rounded-xl bg-white/10 ring-1 ring-white/15 backdrop-blur-xl'
              : 'rounded-xl bg-white shadow-sm ring-1 ring-zinc-200'
          }`}
        >
          <input
            id={emailId}
            name='email'
            type='email'
            required
            autoComplete='off'
            placeholder='Enter your email'
            disabled={isSubmitted}
            value={email}
            onChange={e => setEmail(e.target.value)}
            aria-invalid={message?.type === 'error' || undefined}
            className={`absolute inset-0 appearance-none rounded-xl bg-transparent pr-28 pl-4 text-base outline-none disabled:opacity-60 ${
              isDark
                ? 'text-white placeholder:text-white/55'
                : 'text-zinc-900 placeholder:text-zinc-400'
            }`}
          />
          <div className='absolute inset-y-1 right-1'>
            <InsetSubmitButton
              isDark={isDark}
              disabled={!hasText || isSubmitted}
            />
          </div>
        </div>

        {/* Desktop: stacked-row outlined ghost (existing pattern) */}
        <div className='hidden gap-2 md:flex md:flex-row'>
          <input
            name='email'
            type='email'
            required
            autoComplete='off'
            placeholder='Enter your email'
            disabled={isSubmitted}
            value={email}
            onChange={e => setEmail(e.target.value)}
            aria-invalid={message?.type === 'error' || undefined}
            className={
              isDark
                ? 'h-11 min-h-11 flex-1 appearance-none rounded-lg border border-white/40 bg-transparent px-3 text-base text-white outline-none placeholder:text-white/55 disabled:opacity-60'
                : 'h-11 min-h-11 flex-1 appearance-none rounded-lg border border-zinc-400 bg-transparent px-3 text-base text-zinc-900 outline-none placeholder:text-zinc-400 disabled:opacity-60'
            }
          />
          <SubmitButton isDark={isDark} disabled={!hasText || isSubmitted} />
        </div>
      </form>

      <Toast message={message} stateKey={state.status} />
    </>
  );
}

function Toast({
  message,
  stateKey,
}: {
  message: Message;
  stateKey: WaitlistState['status'];
}) {
  const [visible, setVisible] = useState(false);

  // When state changes to a non-idle status, show the toast and start a
  // dismiss timer. Reset on every state change so back-to-back submissions
  // get a fresh timer instead of inheriting the previous one. This is a
  // legitimate synchronization of external server-action state into the
  // toast's visibility — the lint rule flags as a heuristic but the
  // pattern is correct.
  useEffect(() => {
    if (stateKey === 'idle') return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVisible(true);
    const timer = setTimeout(() => setVisible(false), TOAST_DURATION_MS);
    return () => clearTimeout(timer);
  }, [stateKey]);

  if (!message) return null;

  return (
    <div
      role={message.type === 'error' ? 'alert' : 'status'}
      aria-live='polite'
      aria-atomic='true'
      className={`pointer-events-none fixed inset-x-0 top-4 z-50 flex justify-center px-4 transition-all duration-300 ease-out ${
        visible ? 'translate-y-0 opacity-100' : '-translate-y-3 opacity-0'
      }`}
    >
      <span
        className={`max-w-[90vw] rounded-2xl bg-zinc-900/70 px-4 py-2.5 text-sm font-medium shadow-2xl ring-1 ring-white/10 backdrop-blur-2xl ${toastTextClass(
          message.type
        )}`}
      >
        {message.text}
      </span>
    </div>
  );
}

function toastTextClass(type: MessageType) {
  switch (type) {
    case 'error':
      return 'text-orange-200';
    case 'success':
      return 'text-white';
    case 'info':
      return 'text-white/85';
  }
}

function SubmitButton({
  isDark,
  disabled,
}: {
  isDark: boolean;
  disabled: boolean;
}) {
  const { pending } = useFormStatus();
  return (
    <button
      type='submit'
      disabled={disabled || pending}
      className={
        isDark
          ? 'inline-flex h-11 min-h-11 w-[8rem] cursor-pointer items-center justify-center rounded-lg border border-white/40 bg-transparent px-5 text-sm font-medium text-white transition hover:bg-white/10 disabled:cursor-default disabled:border-white/40 disabled:text-white/55 disabled:hover:bg-transparent'
          : 'inline-flex h-11 min-h-11 w-[8rem] cursor-pointer items-center justify-center rounded-lg border border-zinc-400 bg-transparent px-5 text-sm font-medium text-zinc-900 transition hover:bg-zinc-100 disabled:cursor-default disabled:text-zinc-400 disabled:hover:bg-transparent'
      }
    >
      {pending ? 'Joining…' : 'Join waitlist'}
    </button>
  );
}

function InsetSubmitButton({
  isDark,
  disabled,
}: {
  isDark: boolean;
  disabled: boolean;
}) {
  const { pending } = useFormStatus();
  return (
    <button
      type='submit'
      disabled={disabled || pending}
      className={
        isDark
          ? 'inline-flex h-full w-[5rem] cursor-pointer items-center justify-center rounded-lg bg-white px-4 text-sm font-medium text-zinc-900 shadow-sm transition hover:bg-zinc-100 disabled:cursor-default disabled:bg-white/30 disabled:text-zinc-900/50 disabled:hover:bg-white/30'
          : 'inline-flex h-full w-[5rem] cursor-pointer items-center justify-center rounded-lg bg-zinc-900 px-4 text-sm font-medium text-white shadow-sm transition hover:bg-zinc-800 disabled:cursor-default disabled:bg-zinc-300 disabled:text-zinc-500 disabled:hover:bg-zinc-300'
      }
    >
      {pending ? 'Joining…' : 'Join →'}
    </button>
  );
}
